import { useApolloClient } from "@apollo/client";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import {
  GetChatUnreadMessageCountDocument,
  useGetChatsForUserQuery,
} from "graphql/__generated__/hooks";
import { useChatReadState } from "./useChatReadState";

export type ChatMessageSocketPayload = {
  chatId?: string;
  senderId?: string;
  messageId?: string;
  unreadByChatId?: Record<string, number>;
};

/**
 * Unread message counts (messages from others after local last-read checkpoint).
 * Badge updates: subscribe via `handleChatMessageSocket` from `ChatUnreadSocketListener` (header) after `socketConnected`.
 */
export function useChatUnreadCounts(
  userId: string | undefined,
  socketConnected: boolean,
) {
  const { getLastReadIso, markChatRead, readStateVersion } = useChatReadState(
    userId,
  );
  const client = useApolloClient();
  const activeChatIdRef = useRef<string | null>(null);
  const setActiveChatId = useCallback((chatId: string | null) => {
    activeChatIdRef.current = chatId;
  }, []);

  const { data: chatsData, refetch: refetchChats } = useGetChatsForUserQuery({
    variables: { userId: userId! },
    skip: !userId,
  });

  const chats = chatsData?.chat ?? [];
  const chatsRef = useRef(chats);
  chatsRef.current = chats;

  const chatIdsKey = useMemo(
    () => chats.map((c) => c.chat_id).sort().join(","),
    [chats],
  );

  const [byChatId, setByChatId] = useState<Record<string, number>>({});

  const fetchUnreadCounts = useCallback(async () => {
    if (!userId) {
      setByChatId({});
      return;
    }
    const list = chatsRef.current;
    if (!list.length) {
      setByChatId({});
      return;
    }
    const results = await Promise.all(
      list.map(async (c) => {
        const { data } = await client.query({
          query: GetChatUnreadMessageCountDocument,
          variables: {
            chatId: c.chat_id,
            userId,
            after: getLastReadIso(c.chat_id),
          },
          fetchPolicy: "network-only",
        });
        const count = data?.chat_message?.length ?? 0;
        return [c.chat_id, count] as const;
      }),
    );
    setByChatId(Object.fromEntries(results));
  }, [userId, chatIdsKey, readStateVersion, client, getLastReadIso]);

  useEffect(() => {
    void fetchUnreadCounts();
  }, [fetchUnreadCounts]);

  const refreshUnread = useCallback(() => {
    void refetchChats();
    void fetchUnreadCounts();
  }, [refetchChats, fetchUnreadCounts]);

  const refreshRef = useRef(refreshUnread);
  refreshRef.current = refreshUnread;

  const userIdRef = useRef(userId);
  userIdRef.current = userId;

  const handleChatMessageSocket = useCallback(
    (payload: ChatMessageSocketPayload) => {
      const uid = userIdRef.current;
      const chatId = payload.chatId;
      if (!chatId || !uid) {
        void refreshRef.current();
        return;
      }

      if (
        payload.unreadByChatId &&
        Object.keys(payload.unreadByChatId).length > 0
      ) {
        setByChatId((prev) => ({ ...prev, ...payload.unreadByChatId }));
        void refetchChats();
        return;
      }

      const senderId = payload.senderId;
      if (senderId && senderId !== uid) {
        if (chatId === activeChatIdRef.current) {
          void refreshRef.current();
          return;
        }
        setByChatId((prev) => ({
          ...prev,
          [chatId]: (prev[chatId] ?? 0) + 1,
        }));
        void refetchChats();
        return;
      }

      void refreshRef.current();
    },
    [refetchChats],
  );

  const totalUnread = useMemo(
    () => Object.values(byChatId).reduce((a, b) => a + b, 0),
    [byChatId],
  );

  return {
    totalUnread,
    byChatId,
    markChatRead,
    refreshUnread,
    setActiveChatId,
    socketConnected,
    handleChatMessageSocket,
  };
}
